<template>
    <section class="avail-month">
        <header class="avail-month__weekdays">
            <span v-for="(weekday, index) in weekDays" :key="index" class="avail-month__weekday">
                {{ weekday }}
            </span>
        </header>

        <ol class="avail-month__grid">
            <li v-for="(day, index) in monthDays" :key="index" :class="dayCellClass(day)">
                <month-calendar-day
                    v-if="day"
                    :year="year"
                    :month="month"
                    :day="day"
                    :physician="physician"
                    :practice="practice"
                    :slots="getSlots(day)"
                    :slotsCount="getSlotsCount(day)"
                    :referralsCount="getReferralsCount(day)"
                    :confirmedCount="getConfirmedCount(day)"
                    :pendingCount="getPendingCount(day)"
                    :isUnavailable="getIsUnavailable(day)"
                    :showCancelled="showCancelled"
                    @emitEvent="emitEvent($event)"
                ></month-calendar-day>

                <div v-else class="avail-month__day-pad" @click="navigateToPaddingDate(getPaddingDate(index))">
                    <span class="avail-month__day-pad-num">{{ getPaddingDate(index).date() }}</span>
                </div>
            </li>
        </ol>
    </section>
</template>

<script>
    import dayjs from 'dayjs'
    import CalendarMixin from 'mixins/CalendarMixin'
    import MonthCalendarDay from './AvailabilityMonthCalendarDay'
    import ReferralStatusEnum from '../../../enums/ReferralStatusEnum'

    export default {
        mixins: [CalendarMixin],

        components: {
            MonthCalendarDay,
        },

        props: {
            dates: { type: Object, required: true },
            physician: { type: Object, required: true },
            practice: { type: Object, required: true },
            showCancelled: { type: Boolean, required: true },
        },

        methods: {
            dayCellClass(day) {
                return {
                    'avail-month__cell': true,
                    'avail-month__cell--empty': !day,
                    'avail-month__cell--active': !!day,
                }
            },

            navigateToPaddingDate(date) {
                const dateStr = date.format('YYYY-MM-DD')
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            view: 'daily',
                            date: dateStr,
                        },
                    })
                    .catch(() => {})
            },

            getSlots(day) {
                const date = dayjs().year(this.year).month(this.month).date(day).startOf('day')
                const slots = this.dates[dayjs().year(this.year).month(this.month).date(day).startOf('day').format()] || []
                if (slots.length > 0 && date.isBefore(dayjs(), 'date')) {
                    return slots.filter((slot) => slot.referral)
                }
                return slots
            },

            getSlotsCount(day) {
                return this.getSlots(day).filter((slot) => !slot.referral).length
            },

            getReferralsCount(day) {
                return this.getSlots(day).filter(
                    (slot) =>
                        slot.referral &&
                        slot.referral.status !== ReferralStatusEnum.NO_SHOW &&
                        slot.referral.status !== ReferralStatusEnum.CANCELLED,
                ).length
            },

            getConfirmedCount(day) {
                return this.getSlots(day).filter(
                    (slot) =>
                        slot.referral &&
                        [
                            ReferralStatusEnum.PATIENT_VISIT_PENDING,
                            ReferralStatusEnum.INSURANCE_AUTH_PENDING,
                            ReferralStatusEnum.CONSULTATION_NOTES_PENDING,
                            ReferralStatusEnum.TIME_SLOTS_PROPOSED,
                            ReferralStatusEnum.COMPLETED,
                            ReferralStatusEnum.SCHEDULED,
                        ].includes(slot.referral.status),
                ).length
            },

            getPendingCount(day) {
                return this.getSlots(day).filter(
                    (slot) =>
                        slot.referral &&
                        [
                            ReferralStatusEnum.CREATED,
                            ReferralStatusEnum.RESCHEDULE_REQUESTED,
                            ReferralStatusEnum.RESCHEDULE_REQUESTED_BY_PATIENT,
                            ReferralStatusEnum.RESCHEDULE_AVAILABILITY_CHANGE,
                        ].includes(slot.referral.status),
                ).length
            },

            getIsUnavailable(day) {
                const slots = this.getSlots(day)
                if (slots.length === 0) return false
                return slots.every((slot) => slot.isUnavailable || slot.referral)
            },

            getPaddingDate(index) {
                const obj = this.monthDaysObject()
                const startPaddingLength = obj.startPadding.length
                const startOfCurrentMonth = dayjs().year(this.year).month(this.month).startOf('month')

                if (index < startPaddingLength) {
                    const offset = startPaddingLength - index
                    return startOfCurrentMonth.subtract(offset, 'day')
                }

                return startOfCurrentMonth.add(index - startPaddingLength, 'day')
            },
        },
    }
</script>

<style scoped>
    .avail-month {
        width: 100%;
        padding: 0 24px 24px 24px;
    }

    .avail-month__weekdays {
        display: grid;
        grid-template-columns: repeat(7, minmax(0, 1fr));
        gap: 16px;
        margin-bottom: 16px;
    }

    .avail-month__weekday {
        text-align: center;
        font-weight: 600;
        font-size: 13px;
        color: #4b5563;
        padding: 12px 0;
        text-transform: capitalize;
    }

    .avail-month__grid {
        display: grid;
        grid-template-columns: repeat(7, minmax(0, 1fr));
        gap: 16px;
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .avail-month__cell {
        min-height: 128px;
    }

    .avail-month__cell--empty .avail-month__day-pad {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 12px;
        min-height: 100%;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        cursor: pointer;
        transition: box-shadow 0.15s ease;
    }

    .avail-month__cell--empty .avail-month__day-pad:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .avail-month__day-pad-num {
        font-size: 14px;
        font-weight: 500;
        color: #d1d5db;
    }

    .avail-month__cell--active {
        min-height: 128px;
    }

    @media (max-width: 1024px) {
        .avail-month__weekdays,
        .avail-month__grid {
            gap: 12px;
        }
        .avail-month__cell {
            min-height: 110px;
        }
    }

    @media (max-width: 768px) {
        .avail-month__weekdays,
        .avail-month__grid {
            gap: 8px;
        }
        .avail-month__weekday {
            font-size: 11px;
        }
        .avail-month__cell {
            min-height: 90px;
        }
    }
</style>
